/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { msalInstance, msalRedirectPromise, loginRequest } from '@/lib/msal';
import { logClientError } from '@/lib/clientLog';
import BrandPanel from './login/BrandPanel';
import SignInCard from './login/SignInCard';
import amidaLogo from '@/assets/amida-logo.png';

/**
 * Split-screen login: an Amida-branded marketing panel (large screens) beside a
 * clean, borderless Microsoft SSO sign-in column. Thin orchestrator — holds the
 * MSAL redirect handler and the redirect-error read.
 */
export default function LoginPage() {
  const [params] = useSearchParams();
  const error = params.get('error');
  // `pending` drives the UI (see SignInCard: `disabled` + `aria-busy` + label).
  // Once React has committed it, `disabled` is what actually swallows a second
  // click. `pendingRef` is the same-tick backstop for the window BEFORE that
  // commit: a state value read inside the handler is the one captured in this
  // render's closure, so it is still `false` for a second click that arrives
  // before the re-render — a `if (pending) return` would fall straight through.
  // A ref mutates synchronously, so it holds in that window too.
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);

  // The success path deliberately leaves both latches set, because the browser
  // is navigating away (see the catch below). A back/forward-cache restore —
  // Back out of the Microsoft consent screen — breaks that assumption: it
  // brings this page back WITHOUT re-running the app, so `pending` is still
  // true and the only button on the page renders disabled, captioned
  // "Redirecting to Microsoft…", with the latch refusing every click. That is a
  // permanently dead sign-in button, the exact defect this page was already
  // fixed for once (survey 03-silent-failure #7), and it is reachable only
  // because `disabled={pending}` now exists. `pageshow` with `persisted` is the
  // one signal a bfcache restore gives us; an ordinary reload rebuilds this
  // state from scratch and needs nothing.
  useEffect(() => {
    function reArmAfterBfcacheRestore(event: PageTransitionEvent) {
      if (!event.persisted) return;
      pendingRef.current = false;
      setPending(false);
    }
    window.addEventListener('pageshow', reArmAfterBfcacheRestore);
    return () => window.removeEventListener('pageshow', reArmAfterBfcacheRestore);
  }, []);

  async function handleMicrosoftSignIn() {
    // Why any of this exists: the click awaits `msalRedirectPromise`
    // (initialize() → handleRedirectPromise(), i.e. network work) before the
    // browser navigates, so on a slow connection the button sits there looking
    // untouched for seconds. A second click makes MSAL throw
    // `interaction_in_progress`, which the catch below turns into
    // `?error=login_failed` — "We could not sign you in" shown to someone whose
    // sign-in was in fact progressing. Refusing the second call is what stops
    // the missing feedback from manufacturing a false error.
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith('msal.'))
        .forEach((k) => localStorage.removeItem(k));
      sessionStorage.removeItem('msal.interaction.status');
      await msalRedirectPromise;
      await msalInstance.loginRedirect(loginRequest);
    } catch (err: unknown) {
      // `msalRedirectPromise` is a memoized module-level promise (see msal.ts:
      // a second handleRedirectPromise() would return null because the code was
      // already consumed). After a refused Azure redirect it is already
      // rejected, so without this catch the very first line of the handler threw
      // and `loginRedirect` was never reached: the button did nothing at all,
      // forever, with no spinner and no message (survey 03-silent-failure #7).
      logClientError('Login microsoft sign-in', err);
      // Re-arm the button on the failure path only — both the latch and the
      // rendered state, or the next click hits the ref and returns early. There
      // is deliberately no `finally`: on the success path the browser is already
      // navigating to Microsoft, and clearing these there would un-disable the
      // button during the redirect and reopen the double-click window above.
      pendingRef.current = false;
      setPending(false);
      // A full document load, NOT a react-router navigate. A client-side
      // navigation keeps the rejected module promise in scope and reproduces the
      // dead button on the next click; only a fresh document discards it.
      // `login_failed` is already in SignInCard's error copy table.
      window.location.assign('/login?error=login_failed');
    }
  }

  return (
    <div className="min-h-screen bg-white lg:grid lg:grid-cols-[1.15fr_1fr]">
      <BrandPanel />

      <main className="relative flex min-h-screen flex-col bg-white px-6 py-10 sm:p-10 xl:p-16">
        {/* items-start: in a flex column the default `align-items: stretch`
            beats the image's `w-auto` and distorts the wordmark to the full
            column width. The explicit width pins the 5:1 asset ratio so a
            future layout change cannot stretch it again. */}
        <header className="flex flex-col items-start gap-2">
          <img src={amidaLogo} alt="Amida" width={130} height={26} className="h-[26px] w-[130px]" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#5b6b7a]">
            Amida Technology Solutions
          </span>
        </header>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="flex flex-1 flex-col"
        >
          <SignInCard error={error} onSignIn={handleMicrosoftSignIn} pending={pending} />
        </motion.div>
      </main>
    </div>
  );
}
